import React, { useCallback, useEffect, useMemo } from 'react';
import { basedAxios } from '../../store/Store';
import Meetings from './Meetings';
import { MeetingItem, MeetingStatus } from './types';
import BookingDialog from './BookingDialog';
import EventLabel from '../../components/EventLabel/EventLabel';
import PersonDialog from './PersonDialog';
import NotificationMenu from './NotificationMenu';
import { getUrlParam } from '../../utils/url.utils';
import MeetingDetails from './MeetingDetails';
import cx from 'classnames';
import { getStyleFromSheet } from '../../utils/style.utils';
import ConfirmDialog from './ConfirmDialog';
import Snackbar from '../../components/Snackbar/Snackbar';
import { encodeToBase64 } from '../../utils/encode.utils';

interface IMatchMakingProps {
  orders: any[];
  activePersonNo: number;
  settings: {
    [key: string]: any;
  };
}

export default function MatchMaking({ orders, activePersonNo, settings }: IMatchMakingProps) {
  const [meetings, setMeetings] = React.useState<MeetingItem[]>([]);
  const [calendar, setCalendar] = React.useState([]);
  const [calendarInviteId, setCalendarInviteId] = React.useState('');
  const [orderList, setOrderList] = React.useState<any[]>([]);
  const [approved, setApproved] = React.useState(false);
  const [orderTab, setOrderTab] = React.useState('' as any);
  const [confirmCancellationMeetingId, setConfirmCancellationMeetingId] = React.useState<number | null>(null);
  const [isSyncing, setIsSyncing] = React.useState(false);

  const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
  const [snackbarData, setSnackbarData] = React.useState<any>(null);

  const [focusedMeeting, setFocusedMeeting] = React.useState<any>(null);
  const [undreadMessages, setUnreadMessages] = React.useState<any[]>([]);

  const [selectedOrder, setSelectedOrder] = React.useState<any>(null);
  const [bookingDialogOpen, setBookingDialogOpen] = React.useState(false);

  const [fetched, setFetched] = React.useState(false);
  const [fetching, setFetching] = React.useState(false);

  const [fixedTimeslot, setFixedTimeslot] = React.useState<any>(null);

  const order = useMemo(() => orders?.[activePersonNo], [orders, activePersonNo]);

  const buttonStyle: CSSStyleDeclaration = getStyleFromSheet('button.cms-button');
  const bodyStyle: CSSStyleDeclaration = getStyleFromSheet('.body');

  const getUnreadMessages = useCallback(async () => {
    try {
      const response = await basedAxios.get('./chat/getMessages/' + order.orderId);
      const undreadMessages = response.data.filter((message: any) => !message.seen && message.sender !== order.orderId.toString());
      setUnreadMessages(undreadMessages);
    } catch (error) {}
  }, [order]);

  // const getNewMeetings = useCallback(async () => {
  //   try {
  //     const badgeNo = orders?.[activePersonNo]?.badgeNo;

  //     const response = await basedAxios.get('./matchMaking/getOrder/' + badgeNo);
  //     const newMeetings = response.data?.myMeetings ?? [];
  //     setMeetings(newMeetings);
  //   } catch (error) {
  //     console.error(error);
  //   }
  // }, [orders]);

  const canSync: boolean = useMemo(() => {
    return meetings.some(meeting =>
      meeting.participants?.some((participant: any) => participant.order.orderId === order.orderId && participant.syncedStatus !== meeting.status)
    );
  }, [meetings, order]);

  const getOrderDetails = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setFetching(true);
        }

        const badgeNo = orders?.[activePersonNo]?.badgeNo;

        const code = encodeToBase64(`${orders?.[activePersonNo]?.orderId}:${orders?.[activePersonNo]?.eventId}`);

        const response = await basedAxios.get('./matchMaking/getOrder/' + badgeNo + '?code=' + code);

        const { myMeetings = [], calendarInviteId, availableTimeslots, orderList, hasApprovedMeetingTerms } = response.data;

        const mid = getUrlParam('mid');

        if (mid) {
          const newFocusedMeeting = myMeetings.find(
            (meeting: any) => meeting.meetingId == mid && meeting.status !== MeetingStatus.CANCELLED && meeting.status !== MeetingStatus.BOOKED
          );

          if (newFocusedMeeting) {
            const isRequested =
              newFocusedMeeting.participants?.length > 0 &&
              newFocusedMeeting.participants?.find((p: any) => p.order.orderId === orders?.[activePersonNo].orderId)?.role !== 'booker';

            const location = availableTimeslots.find(
              (timeslot: any) => timeslot?.start === newFocusedMeeting?.start && timeslot.end === newFocusedMeeting?.end
            )?.location;

            setFocusedMeeting({ ...newFocusedMeeting, isRequested, location });
          }
        }

        setMeetings(myMeetings);
        setCalendarInviteId(calendarInviteId);
        setCalendar(availableTimeslots);
        setOrderList(orderList);

        if (hasApprovedMeetingTerms) {
          setApproved(true);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setFetched(true);
        setFetching(false);
      }
    },
    [activePersonNo, orders]
  );

  const handleCancelMeeting = useCallback(
    async (meetingId: number) => {
      try {
        await basedAxios.put(`./matchMaking/cancelMeeting/${meetingId}?orderId=${order.orderId}`);

        const meetingsCopy = [...meetings];
        const meetingIndex = meetingsCopy.findIndex(meeting => meeting.meetingId === meetingId);
        if (meetingIndex !== -1) {
          meetingsCopy[meetingIndex].status = MeetingStatus.CANCELLED;
          setMeetings(meetingsCopy);
          setFocusedMeeting(null);

          // window.location.search = window.location.search.replace(`mid=${meetingId}`, '');
        }
      } catch (error) {
        console.error(error);
      }
    },
    [meetings, order]
  );

  const handleBlockTimeslot = useCallback(
    async (meetingTimeslotId: number) => {
      try {
        await basedAxios.post(`./matchMaking/blockTimeslot/${order.orderId}?t=${meetingTimeslotId}`);

        getOrderDetails(false);
      } catch (error) {
        console.error(error);
      }
    },
    [calendarInviteId, calendar, order]
  );

  const handleAcceptMeeting = useCallback(
    async (meetingId: number) => {
      try {
        await basedAxios.put(`./matchMaking/acceptMeeting/${meetingId}?orderId=${order.orderId}`);

        const meetingsCopy = [...meetings];
        const meetingIndex = meetingsCopy.findIndex(meeting => meeting.meetingId === meetingId);
        if (meetingIndex !== -1) {
          meetingsCopy[meetingIndex].status = MeetingStatus.BOOKED;
          setMeetings(meetingsCopy);
          setFocusedMeeting(null);

          // window.location.search = window.location.search.replace(`mid=${meetingId}`, '');
        }
      } catch (error) {
        console.error(error);
      }
    },
    [meetings, order?.orderId]
  );

  const handleOrderSelect = useCallback(
    (orderId: number, tab: string = '') => {
      const order = orderList.find(x => x.orderId === orderId);
      setSelectedOrder(order);
      setOrderTab(tab);
    },
    [orderList]
  );

  const handleUnblockTimeslot = useCallback(
    async (meetingId: number) => {
      try {
        await basedAxios.post(`./matchMaking/unblockTimeslot/${meetingId}`);

        const meetingsCopy = [...meetings].filter(meeting => meeting.meetingId !== meetingId);
        setMeetings(meetingsCopy);
      } catch (error) {
        console.error(error);
      }
    },
    [calendarInviteId, calendar, meetings]
  );

  const handleConfirmation = useCallback(async () => {
    try {
      await basedAxios.post(`./matchMaking/ConfirmMatchmakingParticipation/${order.orderId}`);
      setApproved(true);
    } catch (error) {}
  }, [order]);

  const syncCalendar = useCallback(async () => {
    try {
      setIsSyncing(true);
      await basedAxios.put(`./matchMaking/SyncCalendarMeetings?orderId=${order.orderId}`);

      setSnackbarData({ message: 'CalendarSynced', severity: 'success', icon: 'check-line' });
      setShowSnackbar(true);
    } catch (error) {
      setSnackbarData({ message: 'CalendarNotSynced', severity: 'error', icon: 'close-line' });
      setShowSnackbar(true);
    } finally {
      setIsSyncing(false);
    }
  }, [order]);

  const handleFixedTimeslot = (timeslot: any) => {
    setFixedTimeslot(timeslot);
    setBookingDialogOpen(true);
  };

  useEffect(() => {
    let interval: any;
    let meetingInterval: any;

    if (orders?.[activePersonNo]?.badgeNo) {
      getUnreadMessages();
      getOrderDetails();
      // interval = setInterval(() => {
      //   getUnreadMessages();
      // }, 5000);

      // meetingInterval = setInterval(() => {
      //   getNewMeetings();
      // }, 30000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (meetingInterval) {
        clearInterval(meetingInterval);
      }
    };
  }, [getUnreadMessages, getOrderDetails, orders, activePersonNo]);

  if (!orders?.[activePersonNo]?.badgeNo) {
    return null;
  }

  if (!approved && fetched) {
    return (
      <div className="flex flex-col gap-y-1 justify-center">
        <h2>
          <EventLabel name="ConfirmParticipationTitle" groupName="CMS.MatchMaking" />
        </h2>
        <p>
          <EventLabel name="ConfirmParticipationText" groupName="CMS.MatchMaking" />
        </p>
        <button className="rounded-md bg-teal-600 text-white my-4" onClick={() => handleConfirmation()}>
          <EventLabel name="ConfirmParticipationButton" groupName="CMS.MatchMaking" />
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="w-full">
        {fetching ? (
          <div className="flex items-start md:items-center gap-y-2 md:justify-end mb-4 flex-col justify-end md:flex-row">
            <div className="flex gap-x-2 items-center justify-start lg:justify-between  w-full md:w-fit">
              <div className="flex items-center gap-x-2">
                <div className="h-10 w-32 rounded-sm bg-neutral-200 " />
                <div className="h-10 w-10 rounded-sm bg-neutral-200 " />
              </div>

              <div className="h-10 w-10 lg:w-32 rounded-sm bg-neutral-200 " />
            </div>
          </div>
        ) : (
          <div className="flex items-start md:items-center gap-y-2 md:justify-end mb-4 flex-col justify-end md:flex-row">
            <div className="flex gap-x-2 items-center justify-start lg:justify-between w-full md:w-fit">
              <div className="flex items-center gap-x-2">
                <button
                  onClick={() => setBookingDialogOpen(true)}
                  className={cx('rounded-md  text-white text-sm flex items-center gap-x-2 ')}
                  style={{
                    backgroundColor: buttonStyle['background-color' as any],
                    color: buttonStyle['color'],
                  }}
                >
                  <i className="ri-user-line block" />
                  <span className="">
                    <EventLabel name="BookingMeeting" groupName="CMS.MatchMaking" />
                  </span>
                </button>

                <NotificationMenu undreadMessages={undreadMessages} orderList={orderList} handleOrderSelect={handleOrderSelect} />
                <button
                  onClick={syncCalendar}
                  disabled={isSyncing || !canSync}
                  className="bg-white border disabled:cursor-not-allowed disabled:opacity-50 gap-x-1 border-divider h-10 w-fit flex-center text-sm rounded-md"
                >
                  {isSyncing ? (
                    <svg
                      className="animate-spin h-4 w-4 mr-0 text-neutral-700"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      width={12}
                      height={12}
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <i className="ri-refresh-line" />
                  )}
                  {isSyncing ? <EventLabel name="Syncing" groupName="CMS.MatchMaking" /> : <EventLabel name="SyncCalendar" groupName="CMS.MatchMaking" />}
                </button>
              </div>
            </div>
          </div>
        )}
        {fetching ? (
          <div className="animate-pulse flex flex-col h-auto border border-divider bg-white rounded-md mb-4">
            <div className="flex">
              <div className="h-8 m-2  bg-neutral-200 rounded w-20" />
              <div className="h-8 m-2 bg-neutral-200 rounded w-20" />
              <div className="h-8 m-2  bg-neutral-200 rounded w-20" />
            </div>
            <div className="flex items-center h-16 p-2">
              <div className="h-full bg-neutral-200 rounded w-full  " />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-md mb-4 border border-divider overflow-hidden">
            <Meetings
              onFixedTimeslot={handleFixedTimeslot}
              onCancelMeeting={meetingId => setConfirmCancellationMeetingId(meetingId)}
              onAcceptMeeting={handleAcceptMeeting}
              myOrder={order}
              meetings={meetings}
              settings={settings}
              onUnblockMeeting={handleUnblockTimeslot}
              calendar={calendar}
              orders={orderList}
              onBlockMeeting={handleBlockTimeslot}
              onSelect={handleOrderSelect}
            />
          </div>
        )}
      </div>
      {bookingDialogOpen && (
        <BookingDialog
          fixedTimeslot={fixedTimeslot}
          myMeetings={meetings}
          availableTimeslots={calendar}
          isOpen={bookingDialogOpen}
          myOrder={order}
          onClose={() => {
            setBookingDialogOpen(false);
            getOrderDetails(false);
            setFixedTimeslot(null);
          }}
          settings={settings}
          style={bodyStyle}
          orderList={orderList}
        />
      )}

      {confirmCancellationMeetingId && (
        <ConfirmDialog
          onConfirm={() => {
            handleCancelMeeting(confirmCancellationMeetingId);
            setConfirmCancellationMeetingId(null);
          }}
          onCancel={() => setConfirmCancellationMeetingId(null)}
        />
      )}

      {selectedOrder && (
        <PersonDialog
          isOpen={!!selectedOrder}
          onClose={() => setSelectedOrder(null)}
          order={selectedOrder}
          availableTimeslots={calendar}
          myMeetings={meetings}
          myOrder={order}
          settings={settings}
          activeTab={orderTab}
          style={bodyStyle}
        />
      )}
      {focusedMeeting && (
        <MeetingDetails
          meeting={focusedMeeting}
          onClose={() => setFocusedMeeting(null)}
          onAcceptMeeting={handleAcceptMeeting}
          onCancelMeeting={(meetingId: number) => setConfirmCancellationMeetingId(meetingId)}
          style={bodyStyle}
        />
      )}
      <Snackbar data={snackbarData} showSnackbar={!!(showSnackbar && snackbarData)} setShowSnackbar={setShowSnackbar} />
    </>
  );
}
